import React, { useEffect, useState, useMemo } from "react";
import { Button, Drawer, Tag } from "@stonelog/stonelog-design-system";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { useSelector, useDispatch } from "react-redux";
import {
  pinsSelector,
  setBouncePin,
  setCenter,
  setZoom,
} from "../../../../../../../features/senninha/pinsSlice";
import { hubsSelector } from "../../../../../../../features/senninha/hubsSlice";
import { toCapitalizeFirstLetterOfEachWord } from "../../../../../../../consumingApi/services/helper";
import machineError from "@assets/imgs/machine-error.svg";
import { format, parseISO } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import * as S from "./styles";

const InfoDrawer = ({ open, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const colorsAndText = {
    instalação: { type: "success" },
    desinstalação: { type: "error" },
    troca: { type: "info" },
    campanha: { type: "default" },
    coleta: { type: "info" },
    entrega: { type: "success" },
    evento: { type: "hint" },
  };

  const { pins, serviceDetails, serviceDetailsStatus } = useSelector(pinsSelector);
  const { selectedHubInfos } = useSelector(hubsSelector);
  const dispatch = useDispatch();

  const hasData = Object.keys(serviceDetails.pinDetails || {}).length > 1;

  const displayIdAtendimento = (id) => {
    if (Array.isArray(id)) return id.join(", ");
    return id || "-";
  };

  const isServiceAble2ShowDetails = (osServico) => {
    const pinServico = serviceDetails.pinDetails?.servico;
    return ["COLETA", "ENTREGA"].includes(pinServico) &&
      serviceDetails.pinIdentifiers.servico?.toLowerCase() !== osServico?.toLowerCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = parseISO(dateStr);
      return format(date, "d 'de' MMM 'de' y", { locale: ptBR });
    } catch { return dateStr; }
  };

  useEffect(() => {
    if (serviceDetails.pinDetails?.oss && serviceDetails.pinIdentifiers) {
      const searchId = serviceDetails.pinIdentifiers.id_os;
      const index = serviceDetails.pinDetails.oss.findIndex((item) =>
        Array.isArray(searchId) ? searchId.includes(item.id_os) : item.id_os === searchId
      );
      setOpenIndex(index !== -1 ? index : null);
    }
  }, [serviceDetails, open]);

  const isColetaNoPolo = serviceDetails.pinDetails?.oss?.some(
    (os) => os.status_pathway === "COLETA NO POLO"
  );

  const clienteNome = isColetaNoPolo
    ? serviceDetails.pinDetails?.cliente_pin_coleta
    : serviceDetails.pinDetails?.cliente;

  const ossToDisplay = useMemo(() => {
    const oss = serviceDetails.pinDetails?.oss || [];
    const searchIds = serviceDetails.pinIdentifiers?.id_os;

    if (Array.isArray(searchIds)) {
      return oss.filter(os => searchIds.includes(os.id_os));
    }

    return oss.filter(os => os.servico !== "COLETA");
  }, [serviceDetails.pinDetails, serviceDetails.pinIdentifiers]);

  return (
    <Drawer.Root
      placement="right"
      open={open}
      onClose={() => onClose()}
      mask={false}
      title={
        <Drawer.Header
          title={`Detalhes${serviceDetails.pinDetails?.cliente_pin_coleta
            ? ` de ${serviceDetails.pinDetails.cliente_pin_coleta}`
            : ""
            }`}
          date={new Date()}
        />
      }
    >
      <Drawer.Body>
        {serviceDetailsStatus === "pending" ? (
          <Drawer.Loading />
        ) : (
          <>
            {serviceDetailsStatus === "fulfilled" && hasData && (
              <S.AllBodyContainer>
                <S.BodyTopContainer>
                  <S.BodyTopContent>
                    <p>Alocado para</p>
                    <span>{toCapitalizeFirstLetterOfEachWord(serviceDetails.pinDetails?.angel)}</span>
                  </S.BodyTopContent>
                  {serviceDetails.pinDetails?.tipo?.toLowerCase() === "adquirencia" && (
                    <S.BodyTopContent>
                      <p>
                        Stonecode
                      </p>
                      <span>
                        {displayIdAtendimento(serviceDetails.pinDetails?.id_atendimento)}
                      </span>
                    </S.BodyTopContent>
                  )}

                  <S.BodyTopContent>
                    <p>
                      {serviceDetails.pinDetails?.tipo?.toLowerCase() === "adquirencia"
                        ? `Quantidade de OS`
                        : `Quantidade de Delivery`}
                    </p>
                    <span>{ossToDisplay.length}</span>
                  </S.BodyTopContent>

                  <S.BodyTopContent>
                    <p>Cliente</p>
                    <span>
                      {toCapitalizeFirstLetterOfEachWord(clienteNome)}
                    </span>
                  </S.BodyTopContent>
                  <S.BodyTopContent>
                    <p>Endereço</p>
                    <span>
                      {!!serviceDetails?.pinDetails
                        ? `${serviceDetails.pinDetails?.endereco}, ${serviceDetails.pinDetails?.numero_endereco} - ${toCapitalizeFirstLetterOfEachWord(serviceDetails.pinDetails?.oss[0]?.referencia)}, ${toCapitalizeFirstLetterOfEachWord(serviceDetails.pinDetails?.oss[0]?.cidade || serviceDetails.pinDetails?.cidade)} - ${serviceDetails.pinDetails?.uf}, ${serviceDetails.pinDetails?.cep}`
                        : "-"}
                    </span>
                  </S.BodyTopContent>
                  <S.BodyTopContent>
                    <p>Polo</p>
                    <span>{toCapitalizeFirstLetterOfEachWord(selectedHubInfos.children)}</span>
                  </S.BodyTopContent>
                </S.BodyTopContainer>
                {ossToDisplay.map((os, idx) => (
                  <S.BodyMiddleContainer key={idx}>
                    <S.BodyMiddleHeader
                      onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                      style={{ cursor: "pointer" }}
                    >
                      <div>
                        <p>
                          {serviceDetails.pinDetails?.tipo?.toLowerCase() === "adquirencia"
                            ? "Nº da OS"
                            : "Tracking code"}
                        </p>
                        <span>
                          {os?.servico === "ENTREGA"
                            ? os?.id_atendimento
                            : os?.id_os || os?.id_atendimento}
                        </span>
                      </div>

                      <S.BodyMiddleRightHeader>
                        {os?.servico && (
                          <Tag
                            text={toCapitalizeFirstLetterOfEachWord(os.servico)}
                            type={colorsAndText[os.servico?.toLowerCase()]?.type || "default"}
                          />
                        )}
                        {(isServiceAble2ShowDetails(os.servico) || isColetaNoPolo) && (
                          <Button
                            width="100%"
                            size="small"
                            solid
                            onClick={(e) => {
                              e.stopPropagation();

                              const correctPin = pins.find((pin) => {
                                const hasMatchingOs = pin.oss?.some((pinOs) => pinOs.id_os === os.id_os);

                                if (isColetaNoPolo) {
                                  return hasMatchingOs;
                                }

                                return pin.id !== serviceDetails?.pinDetails?.id && hasMatchingOs;
                              });

                              if (correctPin) {
                                dispatch(setBouncePin(null));

                                requestAnimationFrame(() => {
                                  dispatch(setBouncePin(correctPin.id));
                                });

                                dispatch(
                                  setCenter({
                                    center: {
                                      lat: parseFloat(correctPin.lat),
                                      lng: parseFloat(correctPin.lng),
                                    },
                                  })
                                );

                                dispatch(setZoom(20));
                              }
                            }}
                          >
                            Mostrar no mapa
                          </Button>
                        )}
                      </S.BodyMiddleRightHeader>
                      <GingaIcon
                        name="chevron-down"
                        color="#A6AEBA"
                        size="24px"
                        style={{
                          transition: "all 0.5s ease",
                          transform: `rotate(${openIndex === idx ? "0.5turn" : "1turn"})`,
                        }}
                      />
                    </S.BodyMiddleHeader>
                    <S.BodyMiddleContent open={openIndex === idx}>
                      <div>
                        <p>Data limite cliente</p>
                        <span>{formatDate(os?.customer_deadline_date)}</span>
                      </div>
                      <div>
                        <p>Data limite</p>
                        <span>{formatDate(os?.deadline_date)}</span>
                      </div>
                      <div>
                        <p>Contratante</p>
                        <span>{toCapitalizeFirstLetterOfEachWord(serviceDetails.pinDetails?.contratante)}</span>
                      </div>
                      <div>
                        <p>Tecnologia</p>
                        <span>{os.tec_entrada || os.tec_saida || "-"}</span>
                      </div>
                      <div>
                        <p>Horário de funcionamento</p>
                        <span>{os?.horario_funcionamento}</span>
                      </div>
                      {os?.pacote && (
                        <div>
                          <p>Quantidade de pacote</p>
                          <span>{os.pacote}</span>
                        </div>
                      )}
                      {os?.servico === "ENVIO DE SUPRIMENTOS" && (
                        <div>
                          <p>Quantidade de kits</p>
                          <span>{serviceDetails.pinDetails.tecnologias.length}</span>
                        </div>
                      )}

                    </S.BodyMiddleContent>
                  </S.BodyMiddleContainer>
                ))}
              </S.AllBodyContainer>
            )}

            {serviceDetailsStatus === "fulfilled" && !hasData && (
              <Drawer.Response
                element={<img src={machineError} />}
                message="Não foi possível encontrar os dados deste pin."
              />
            )}
          </>
        )}
      </Drawer.Body>
      <Drawer.Footer>
        <Button width="100%" height="52px" size="large" onClick={() => onClose()}>
          Fechar
        </Button>
      </Drawer.Footer>
    </Drawer.Root>
  );
};

export default InfoDrawer;
